import React, { useState, useContext } from 'react';
import { Star, Flag, Edit2, Trash2, Calendar, User } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../contexts/AuthContext';

/**
 * Individual Professor Rating Item Component
 * Displays a single rating with report functionality
 */
const ProfessorRatingItem = ({ rating, onEdit, onDelete, onReport }) => {
  const { user } = useContext(AuthContext);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  // Render star rating
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={`${
          i < rating
            ? 'text-yellow-500 fill-current'
            : 'text-gray-300 dark:text-gray-600'
        }`}
      />
    ));
  };

  // Handle report submission
  const handleReportSubmit = async (e) => {
    e.preventDefault();
    
    if (!reportReason.trim()) {
      toast.error('Please provide a reason for reporting this rating');
      return;
    }

    try {
      setSubmittingReport(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/professor-ratings/${rating.id}/report`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: reportReason.trim() })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to report rating');
      }
      
      toast.success('Rating reported successfully');
      setShowReportModal(false);
      setReportReason('');
      
      // Call the optional onReport callback
      if (onReport) {
        onReport(rating.id);
      }
    } catch (error) {
      console.error('Error reporting rating:', error);
      toast.error(error.message || 'Failed to report rating');
    } finally {
      setSubmittingReport(false);
    }
  };

  // Check if current user owns this rating
  const isOwner = user && user.id === rating.userId;
  const canModerate = user && (user.role === 'admin' || user.role === 'moderator');

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center space-x-2">
          <div className="flex items-center">
            {renderStars(rating.rating)}
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {rating.rating}/5
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Edit and Delete buttons for rating owner */}
          {isOwner && (
            <>
              {onEdit && (
                <button
                  onClick={() => onEdit(rating)}
                  className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  title="Edit rating"
                >
                  <Edit2 size={16} />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(rating)}
                  className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  title="Delete rating"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </>
          )}
          
          {/* Report button for other users */}
          {!isOwner && user && (
            <button
              onClick={() => setShowReportModal(true)}
              className="p-1 text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors"
              title="Report rating"
            >
              <Flag size={16} />
            </button>
          )}
        </div>
      </div>
      
      {/* Rating review text */}
      {rating.review && (
        <div className="mb-3">
          <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
            "{rating.review}"
          </p>
        </div>
      )}
      
      {/* Rating metadata */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <User size={12} className="mr-1" />
            <span>By {rating.userName}</span>
          </div>
          <div className="flex items-center">
            <Calendar size={12} className="mr-1" />
            <span>{new Date(rating.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
        
        {rating.updatedAt !== rating.createdAt && (
          <span className="text-gray-400 dark:text-gray-500">
            Edited {new Date(rating.updatedAt).toLocaleDateString()}
          </span>
        )}
      </div>
      
      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <Flag className="h-6 w-6 text-red-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Report Rating
              </h3>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please provide a reason for reporting this professor rating. Reports are reviewed by moderators.
            </p>
            
            <form onSubmit={handleReportSubmit}>
              <div className="mb-4">
                <label htmlFor="report-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for reporting *
                </label>
                <textarea
                  id="report-reason"
                  rows="4"
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="Describe why you are reporting this rating..."
                  required
                  disabled={submittingReport}
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason('');
                  }}
                  disabled={submittingReport}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingReport || !reportReason.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingReport ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessorRatingItem;